import { useQuery } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"
import { api, type Registry } from "@/api.js"
import { Async } from "@/components/Async.js"
import { Flag } from "@/components/Flag.js"
import { PageHeader, Section } from "@/components/Page.js"
import { Badge } from "@/components/ui/badge.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { plural } from "@/lib/plural.js"
import { AddCountry } from "./config/Countries.js"
import { useDraft } from "./config/draft.js"
import { coversCountry, type EditorCountry, enabledCount, fromDocument } from "./config/model.js"
import { PublishPanel } from "./config/PublishPanel.js"

/**
 * What the system is set up with, as opposed to what its rules do.
 *
 * **Which countries SAURON serves is a property of the whole system**, not a
 * detail of one country's rule set — so it does not belong on a screen whose
 * subject is the rules of the country you happen to be looking at. That is all
 * this screen is today, and it is where anything else system-wide goes.
 *
 * It edits the same versioned document as Konfiguracja and publishes it the
 * same way; `useDraft` is what keeps the two from stepping on each other.
 */
export function SettingsScreen() {
  const registry = useQuery({ queryKey: ["registry"], queryFn: api.registry })
  const versions = useQuery({ queryKey: ["config"], queryFn: api.configVersions })

  return (
    <Async state={registry}>
      {(spec) => (
        <Async state={versions}>
          {(page) => (
            <Settings
              registry={spec}
              document={(page.items[0]?.config as Record<string, unknown>) ?? {}}
              version={page.items[0]?.version ?? null}
            />
          )}
        </Async>
      )}
    </Async>
  )
}

function Settings({
  registry,
  document,
  version,
}: {
  registry: Registry
  document: Record<string, unknown>
  version: number | null
}) {
  const draft = useDraft(document, registry, version)

  function addCountry(code: string) {
    if (!/^[A-Z]{2}$/.test(code) || draft.countries.some((entry) => entry.code === code)) return
    draft.setCountries([
      ...draft.countries,
      {
        code,
        // Seeded through `fromDocument` for that country, so its sets hold
        // exactly the rules its sources can answer for — and nothing else.
        questions: fromDocument({ countries: { [code]: {} } }, registry)[0]?.questions ?? [],
      },
    ])
  }

  function removeCountry(code: string) {
    if (draft.countries.length <= 1) return
    draft.setCountries(draft.countries.filter((entry) => entry.code !== code))
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Ustawienia"
        actions={
          <>
            {draft.changes.length > 0 && (
              <Badge tone="brand" glyph="●">
                {draft.changes.length} {plural(draft.changes.length, "zmiana", "zmiany", "zmian")}
              </Badge>
            )}
            <span className="font-mono text-[13px] text-muted-foreground">
              {version === null ? "brak wersji" : `wersja ${version}`}
            </span>
          </>
        }
      />

      {/*
       * Grouped from the start, because this screen is where system-wide
       * settings accumulate and a flat stack stops being readable at the third
       * one. A group is a subject; the sections inside it are the settings.
       */}
      <Group title="Zakres">
        <Section
          title="Obsługiwane kraje"
          hint="Można dodać tylko kraj, który obsługuje jakieś zainstalowane źródło."
          actions={
            <AddCountry countries={draft.countries} registry={registry} onAdd={addCountry} />
          }
        >
          <CountryTable countries={draft.countries} registry={registry} onRemove={removeCountry} />
        </Section>

        <Section
          title="Kraj spoza listy"
          hint="Czym odpowiadamy, gdy pyta ktoś stamtąd. To nie to samo co fail-closed."
        >
          <Select value={draft.policy} onValueChange={draft.setPolicy}>
            <SelectTrigger className="w-56" aria-label="Kraj spoza listy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="prepayment">Przedpłata</SelectItem>
              <SelectItem value="allow">Odroczenie</SelectItem>
            </SelectContent>
          </Select>
        </Section>
      </Group>

      <PublishPanel draft={draft} version={version} />
    </div>
  )
}

/**
 * A subject, with its settings under it. One level above `Section`, so a screen
 * that grows to four subjects still reads as four things rather than twelve.
 */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-5 border-t border-border pt-7 first:border-0 first:pt-0">
      <h2 className="text-[17px] font-semibold tracking-tight text-foreground">{title}</h2>
      {children}
    </section>
  )
}

/**
 * The countries, and **what each of them can actually be served by**. The
 * sources column is what makes the add control self-explanatory: a country no
 * source covers has an empty rule set by construction, whatever is configured
 * in it.
 */
function CountryTable({
  countries,
  registry,
  onRemove,
}: {
  countries: readonly EditorCountry[]
  registry: Registry
  onRemove: (code: string) => void
}) {
  return (
    <Table>
      <thead>
        <tr>
          <Th className="w-20">Kraj</Th>
          <Th className="w-24">Reguły</Th>
          <Th>Źródła, które go obsługują</Th>
          <Th className="w-20" />
        </tr>
      </thead>
      <tbody>
        {countries.map((country) => {
          const serving = registry.providers.filter((provider) =>
            coversCountry(provider, country.code),
          )

          return (
            <Tr key={country.code}>
              <Td className="py-2 font-mono text-[13px] font-medium">
                <span className="flex items-center gap-2">
                  <Flag code={country.code} />
                  {country.code}
                </span>
              </Td>
              <Td className="py-2 tabular-nums">{enabledCount(country)}</Td>
              <Td className="py-2">
                {serving.length === 0 ? (
                  <span className="text-[13px] text-critical">
                    żadne — zestaw zawsze przedpłaci
                  </span>
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {serving.map((provider) => (
                      <Badge
                        key={provider.providerId}
                        tone="neutral"
                        className="font-mono text-[11.5px]"
                      >
                        {provider.providerId}
                      </Badge>
                    ))}
                  </span>
                )}
              </Td>
              <Td className="py-2 text-right">
                <RemoveButton
                  code={country.code}
                  last={countries.length <= 1}
                  onRemove={onRemove}
                />
              </Td>
            </Tr>
          )
        })}
      </tbody>
    </Table>
  )
}

/**
 * Removing a country, and **answering where it was asked** when the last one
 * cannot go.
 *
 * The button used to be disabled with the reason in a `title`, and that is the
 * shut control this panel keeps having reported as broken — the country picker
 * before it, then the rule editor's mode select. Nobody hovers something that
 * looks unavailable, and a disabled button does not reliably show a tooltip at
 * all. So the control stays live and the click is what produces the sentence,
 * beside the button rather than instead of it. The refusal itself is unchanged:
 * with no country at all every decision falls to the uncovered-country policy.
 */
function RemoveButton({
  code,
  last,
  onRemove,
}: {
  code: string
  /** The only rule set left, which is the one that cannot be removed. */
  last: boolean
  onRemove: (code: string) => void
}) {
  const [refused, setRefused] = useState(false)

  return (
    <span className="flex items-center justify-end gap-2.5">
      {/*
       * Gated on `last` as well as the click, so adding a second country clears
       * the message by making it untrue — there is nothing to dismiss and no
       * state to reset.
       */}
      {last && refused && (
        <span role="status" className="text-[13px] leading-5 text-muted-foreground">
          Ostatni zestaw zostaje — bez żadnego kraju każda decyzja odpowiada przedpłatą.
        </span>
      )}
      <button
        type="button"
        aria-label={`Usuń kraj ${code}`}
        className="text-[13px] text-muted-foreground hover:text-critical"
        onClick={() => (last ? setRefused(true) : onRemove(code))}
      >
        Usuń
      </button>
    </span>
  )
}
